<template>
    <div class="tw-px-4">
        <div class="tw-grid tw-grid-cols-12 tw-gap-4">
            <!-- Left Sidebar - Filters -->
            <div class="tw-col-span-12 lg:tw-col-span-3">
                <div class="tw-bg-white tw-border tw-border-primary-200 tw-rounded-lg tw-p-4 tw-sticky tw-top-4 tw-min-h-[calc(100vh-200px)]">
                    <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">
                        {{ $t('common.filters', 'Filters') }}
                    </h3>
                    
                    <!-- Search Filter -->
                    <div class="tw-mb-4">
                        <FormField 
                            v-model="search" 
                            type="text" 
                            :hide-label="true" 
                            :hide-errors="true" 
                            :placeholder="$t('common.search-services', 'Search services...')"
                            left-icon="solar:magnifer-linear" 
                            @input="onSearch" 
                            class="tw-bg-gray-50 tw-rounded-md" 
                        />
                    </div>

                    <!-- Category Filter -->
                    <div class="tw-mb-4">
                        <FormMultiSelect
                            v-model="selectedCategory"
                            :options="categoryOptions"
                            :placeholder="$t('common.filter-by-category', 'Filter by category')"
                            left-icon="solar:widget-bold-duotone"
                            class="tw-bg-white tw-rounded-md"
                            :hide-errors="true"
                            option-value="id"
                            @change="onFilterChange"
                        />
                    </div>

                    <!-- Country Filter -->
                    <div class="tw-mb-4">
                        <FormMultiSelect
                            v-model="selectedCountry"
                            :options="countryOptions"
                            :placeholder="$t('common.filter-by-country', 'Filter by country')"
                            left-icon="solar:flag-bold-duotone"
                            class="tw-bg-white tw-rounded-md"
                            :hide-errors="true"
                            option-value="id"
                            @change="onFilterChange"
                        />
                    </div>

                    <!-- Certificates Filter -->
                    <div class="tw-mb-4">
                        <FormMultiSelect
                            v-model="selectedCertificate"
                            :options="certificateOptions"
                            :placeholder="$t('common.filter-by-certificate', 'Filter by certificate')"
                            left-icon="solar:medal-ribbons-star-bold-duotone"
                            class="tw-bg-white tw-rounded-md"
                            :hide-errors="true"
                            option-value="id"
                            @change="onFilterChange"
                        />
                    </div>

                    <!-- Clear Filters button -->
                    <div class="tw-mb-4 tw-flex tw-justify-center">
                        <a-button @click="resetFilters" intent="link" left-icon="solar:refresh-bold-duotone">
                            {{ $t('buttons.clear-filters', 'Clear Filters') }}
                        </a-button>
                    </div>
                </div>
            </div>

            <!-- Right Side - Services Grid -->
            <div class="tw-col-span-12 lg:tw-col-span-9">
                <!-- Loading State -->
                <div v-if="loading && services.length === 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    <div v-for="i in 12" :key="i" class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm tw-overflow-hidden">
                        <div class="tw-p-4 tw-border-b tw-border-gray-100">
                            <div class="tw-flex tw-items-start tw-gap-3">
                                <div class="tw-w-full tw-h-[128px] tw-rounded-lg tw-bg-gray-200 tw-animate-pulse"></div>
                            </div>
                        </div>
                        <div class="tw-p-4 tw-space-y-3">
                            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2"></div>
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <!-- Services Grid -->
                <div v-else-if="services.length > 0" ref="servicesGrid" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    <ServiceCard
                        v-for="service in services"
                        :key="service.id"
                        :service="service"
                        @view="openService"
                    />
                </div>

                <!-- Empty State -->
                <div v-else class="tw-text-center tw-py-12 tw-min-h-[calc(100vh-300px)] tw-flex tw-items-center tw-justify-center">
                    <div class="tw-max-w-md tw-mx-auto">
                        <img :src="$frontend('assets/images/empty-state/companies.svg')" class="tw-w-full tw-max-w-[200px] tw-h-full tw-object-cover tw-rounded-lg tw-mx-auto tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                            {{ $t('messages.no-services-found', 'No services found') }}
                        </h3>
                        <p class="tw-text-gray-500 tw-mb-6">
                            {{ $t('messages.no-services-description', 'Try adjusting your search or filters') }}
                        </p>
                        <a-button 
                            @click="resetFilters" 
                            intent="primary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            {{ $t('buttons.reset-filters', 'Reset Filters') }}
                        </a-button>
                    </div>
                </div>

                <!-- Load More Loading -->
                <div v-if="loadingMore && services.length > 0" class="tw-text-center tw-mt-8">
                    <div class="tw-flex tw-items-center tw-justify-center tw-py-4">
                        <svg class="tw-animate-spin tw-h-8 tw-w-8 tw-text-primary-500" fill="none" viewBox="0 0 24 24">
                            <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                        </svg>
                        <span class="tw-ml-2 tw-text-gray-600">{{ $t('common.loading-more', 'Loading more services...') }}</span>
                    </div>
                </div>

                <!-- End of Results -->
                <div v-if="!hasMorePages && services.length > 0" class="tw-text-center tw-mt-8 tw-py-4">
                    <p class="tw-text-sm tw-text-gray-500">
                        {{ $t('messages.end-of-results', 'You\'ve reached the end of the results') }}
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- Service View Modal -->
    <ServiceViewModal
        v-if="selectedService"
        :visible="showServiceModal"
        :service="selectedService"
        @close="closeService"
        @bookmark-changed="onBookmarkChanged"
    />
</template>

<script setup>
import { ref, onMounted, watch, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormMultiSelect from '@/components/form/FormMultiSelect.vue';
import ServiceCard from '@/components/services/ServiceCard.vue';
import ServiceViewModal from '@/components/services/ServiceViewModal.vue';
import { paginate as servicesApi } from '@/api/services';
import appApi from '@/api/app';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const loadingMore = ref(false);
const services = ref([]);
const categories = ref([]);
const search = ref('');
const selectedCategory = ref([]);
const countries = ref([]);
const certificates = ref([]);
const selectedCountry = ref([]);
const selectedCertificate = ref([]);
const servicesGrid = ref(null);

// Pagination
const currentPage = ref(1);
const hasMorePages = ref(true);
const perPage = 12; // 4 items per row * 3 rows initially

// Modal state
const selectedService  = ref(null);
const showServiceModal = ref(false);

const openService = (service) => {
    selectedService.value  = service;
    showServiceModal.value = true;
};

const closeService = () => {
    showServiceModal.value = false;
};

const onBookmarkChanged = ({ id, bookmarked }) => {
    const s = services.value.find(s => s.id === id);
    if (s) s.is_bookmarked = bookmarked;
};

// Search debounce
let searchTimeout = null;

// Computed properties
const categoryOptions = computed(() => {
    const options = [];
    
    if (categories.value.length > 0) {
        options.push(...categories.value.map(cat => ({
            id: cat.id,
            name: cat.name
        })));
    }
    
    return options;
});

const countryOptions = computed(() => {
    return countries.value.map(c => ({ id: c.id, name: c.name }));
});

const certificateOptions = computed(() => {
    return certificates.value.map(c => ({ id: c.id, name: c.name }));
});

// Methods
const fetchCategories = async () => {
    try {
        const response = await appApi.forms.categories('service');
        categories.value = response.data || [];
    } catch (error) {
        console.error('Error fetching categories:', error);
        useAlert(t('errors.failed-to-load-categories', 'Failed to load categories'), 'danger');
    }
};

const fetchCountries = async () => {
    try {
        const response = await appApi.forms.countries();
        countries.value = response.data || [];
    } catch (error) {
        console.error('Error fetching countries:', error);
        useAlert(t('errors.failed-to-load-countries', 'Failed to load countries'), 'danger');
    }
};

const fetchCertificates = async () => {
    try {
        const response = await appApi.forms.certificates('service');
        certificates.value = response.data || [];
    } catch (error) {
        console.error('Error fetching certificates:', error);
        useAlert(t('errors.failed-to-load-certificates', 'Failed to load certificates'), 'danger');
    }
};

const fetchAllFilters = async () => {
    await Promise.all([
        fetchCategories(),
        fetchCountries(),
        fetchCertificates()
    ]);
};

const getSelectedIds = (arr) => arr.map(item => typeof item === 'object' ? item.id : item);

const fetchServices = async (page = 1, append = false) => {
    if (page === 1) {
        loading.value = true;
    } else {
        loadingMore.value = true;
    }

    try {
        const params = {
            page,
            per_page: perPage,
            search: search.value.trim(),
            category_id: selectedCategory.value.length ? getSelectedIds(selectedCategory.value) : [],
            country_id: selectedCountry.value.length ? getSelectedIds(selectedCountry.value) : [],
            certificate_id: selectedCertificate.value.length ? getSelectedIds(selectedCertificate.value) : [],
        };

        const response = await servicesApi(params);

        if (response.data) {
            // Map API response to component structure - same as CompanyServiceListView
            const fetchedServices = (response.data.data || []).map(service => {
                return {
                    ...service,
                    id: service.id,
                    name: service.name, // Keep original name
                    title: service.name, // Also map to title for compatibility
                    description: service.description,
                    image: service.image, // Services use 'image' instead of 'thumbnail_url'
                    status: service.status,
                    created_at: service.created_at,
                    createdDate: service.created_at, // Also map to createdDate for compatibility
                    categories: service.categories || [],
                    company: service.company || null
                }
            });
            
            if (append) {
                services.value.push(...fetchedServices);
            } else {
                services.value = fetchedServices;
            }

            // Check if there are more pages - same as CompanyServiceListView
            hasMorePages.value = response.data.current_page < response.data.last_page;
            currentPage.value = response.data.current_page;
        }
    } catch (error) {
        console.error('Error fetching services:', error);
        useAlert(t('errors.failed-to-load-services', 'Failed to load services'), 'danger');
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        hasMorePages.value = true;
        fetchServices(1);
    }, 500);
};

const onFilterChange = () => {
    currentPage.value = 1;
    hasMorePages.value = true;
    fetchServices(1);
};

const resetFilters = () => {
    search.value = '';
    selectedCategory.value = [];
    selectedCountry.value = [];
    selectedCertificate.value = [];
    currentPage.value = 1;
    hasMorePages.value = true;
    fetchServices(1);
};

// Scroll-based load more functionality
const handleScroll = () => {
    if (loadingMore.value || !hasMorePages.value) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.offsetHeight;

    // Load more when user is 200px from bottom
    if (scrollTop + windowHeight >= documentHeight - 200) {
        loadMore();
    }
};

const loadMore = () => {
    if (!loadingMore.value && hasMorePages.value) {
        fetchServices(currentPage.value + 1, true);
    }
};

// Initialize
onMounted(async () => {
    await Promise.all([
        fetchAllFilters(),
        fetchServices()
    ]);
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    // Remove scroll listener
    window.removeEventListener('scroll', handleScroll);
    clearTimeout(searchTimeout);
});

// Watchers for filters
watch(search, () => {
    onSearch();
});

watch(selectedCategory, () => {
    onFilterChange();
});

watch(selectedCountry, () => {
    onFilterChange();
});

watch(selectedCertificate, () => {
    onFilterChange();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>